/** Shared, lossless interpretation of authoring marks. Source text and block ids stay intact. */
import type { Block, HeadingBlock, InlineContent, ListItem } from './astTypes'
import { getBlockText, getInlineText } from './astUtils'

export type WritingKind = 'note' | 'request'
export interface WritingRange {
  start: number
  end: number
  kind: WritingKind
}
export interface WritingIssue {
  blockId: string
  message: string
}
export interface WritingSection {
  heading: HeadingBlock
  start: number
  end: number
  closed: boolean
}
export interface WritingAnalysis {
  ranges: Map<string, WritingRange[]>
  delimiters: Map<string, WritingRange[]>
  hiddenBlocks: Set<string>
  boundaryBlocks: Set<string>
  sections: WritingSection[]
  issues: WritingIssue[]
}

export function parseSectionOpening(text: string): { title: string; readiness?: number } | null {
  // Unrated headings may omit the space; reserved operators and digits may not be titles without it.
  const match = text.trimStart().match(/^\^(?:([1-5])\s+|(?=[^0-9^[{(])\s*)(\S[\s\S]*)$/)
  return match ? { title: match[2].trim(), ...(match[1] ? { readiness: Number(match[1]) } : {}) } : null
}

export function isSectionEnd(block: Block): boolean {
  return block.type === 'paragraph' && getBlockText(block).trim() === '^^'
}

export function inferSectionLevel(blocks: Block[], beforeIndex = blocks.length): HeadingBlock['level'] {
  const analysis = analyzeWritingSyntax(blocks.slice(0, beforeIndex))
  for (let i = beforeIndex - 1; i >= 0; i--) {
    const block = blocks[i]
    if (block.type !== 'heading' || analysis.hiddenBlocks.has(block.id)) {
      continue
    }
    const enclosing = analysis.sections.find((s) => s.start < i && i < s.end)
    if (enclosing) {
      return enclosing.heading.level
    }
    return parseSectionOpening(getBlockText(block))
      ? block.level
      : (Math.min(6, block.level + 1) as HeadingBlock['level'])
  }
  return 3
}

/** Runs across paragraphs, formatting nodes and lists. Links/encryption are opaque to delimiter parsing. */
export function analyzeWritingSyntax(blocks: Block[]): WritingAnalysis {
  const out: WritingAnalysis = {
    ranges: new Map(),
    delimiters: new Map(),
    hiddenBlocks: new Set(),
    boundaryBlocks: new Set(),
    sections: [],
    issues: [],
  }
  let note: { kind: WritingKind; close: string; open: string; depth: number; blockId: string } | null = null
  let codeTicks = 0
  let section: WritingSection | null = null

  const visit = (id: string, content: InlineContent[]): void => {
    const text = getInlineText(content)
    const syntax = content
      .map((n) => (n.type === 'text' ? n.text : '\uFFFC'.repeat(getInlineText([n]).length)))
      .join('')
    const ranges: WritingRange[] = []
    const delimiters: WritingRange[] = []
    let start = note ? 0 : -1
    let kind: WritingKind = note?.kind ?? 'note'
    for (let i = 0; i < syntax.length; i++) {
      const char = syntax[i]
      if (char === '\\') {
        i++
        continue
      }
      if (!note && char === '`') {
        let length = 1
        while (syntax[i + length] === '`') {
          length++
        }
        if (!codeTicks) {
          if (length < 3 && syntax.indexOf('`'.repeat(length), i + length) < 0) {
            continue
          }
          codeTicks = length
        } else if (length === codeTicks) {
          codeTicks = 0
        }
        i += length - 1
        continue
      }
      if (codeTicks) {
        continue
      }
      if (!note && char === '^' && (syntax[i + 1] === '{' || syntax[i + 1] === '[')) {
        kind = syntax[i + 1] === '{' ? 'note' : 'request'
        start = i
        delimiters.push({ start: i, end: i + 2, kind })
        note = { kind, open: syntax[i + 1], close: kind === 'note' ? '}' : ']', depth: 1, blockId: id }
        i++
      } else if (note) {
        if (char === note.open) {
          note.depth++
        } else if (char === note.close && --note.depth === 0) {
          delimiters.push({ start: i, end: i + 1, kind })
          ranges.push({ start, end: i + 1, kind })
          note = null
          start = -1
        }
      }
    }
    if (note && start >= 0) {
      ranges.push({ start, end: text.length, kind })
    }
    if (ranges.length) {
      out.ranges.set(id, ranges)
    }
    if (delimiters.length) {
      out.delimiters.set(id, delimiters)
    }
  }
  const visitItems = (items: ListItem[]): void => {
    for (const item of items) {
      visit(item.id, item.content)
      if (item.children) {
        visitItems(item.children)
      }
    }
  }

  blocks.forEach((block, i) => {
    const insideNote = Boolean(note)
    if (!note && !codeTicks) {
      if (block.type === 'heading' && parseSectionOpening(getBlockText(block))) {
        if (section) {
          section.end = i
          out.issues.push({ blockId: section.heading.id, message: 'חסר סימן סיום ^^ לפני המקטע הבא' })
        }
        section = { heading: block, start: i, end: blocks.length, closed: false }
        out.sections.push(section)
      } else if (isSectionEnd(block)) {
        out.boundaryBlocks.add(block.id)
        if (section) {
          section.end = i + 1
          section.closed = true
          section = null
        } else {
          out.issues.push({ blockId: block.id, message: 'סיום מקטע ללא פתיחה' })
        }
        return
      }
    }
    if ('content' in block) {
      visit(block.id, block.content)
      const ranges = out.ranges.get(block.id)
      if (ranges?.length === 1 && ranges[0].start === 0 && ranges[0].end === getBlockText(block).length) {
        out.hiddenBlocks.add(block.id)
      }
    } else if (block.type === 'list') {
      visitItems(block.items)
    } else {
      if (insideNote) {
        out.hiddenBlocks.add(block.id)
      }
      if (block.type === 'image' && block.caption) {
        visit(block.id, block.caption)
      }
    }
  })
  if (note) {
    const open = note as { blockId: string; kind: WritingKind }
    out.issues.push({ blockId: open.blockId, message: open.kind === 'note' ? 'הערה שלא נסגרה' : 'בקשה שלא נסגרה' })
  }
  if (section) {
    const open = section as WritingSection
    out.issues.push({ blockId: open.heading.id, message: 'חסר סימן סיום ^^' })
  }
  return out
}

/** Remove only private source ranges, preserving all surviving marks, links and encryption nodes. */
export function stripWritingNotes(blocks: Block[], keepBoundaries = false, hideUnclosedSections = false): Block[] {
  const analysis = analyzeWritingSyntax(blocks)
  if (hideUnclosedSections) {
    for (const section of analysis.sections.filter((s) => !s.closed)) {
      blocks.slice(section.start, section.end).forEach((b) => analysis.hiddenBlocks.add(b.id))
    }
  }
  const stripInline = (id: string, content: InlineContent[]): InlineContent[] => {
    const ranges = analysis.ranges.get(id) ?? []
    let offset = 0
    return content.flatMap((node): InlineContent[] => {
      const length = getInlineText([node]).length
      const start = offset
      offset += length
      const overlaps = ranges.filter((r) => r.start < offset && r.end > start)
      if (!overlaps.length) {
        return [node]
      }
      if (node.type !== 'text') {
        return []
      }
      const kept: InlineContent[] = []
      let cursor = 0
      for (const r of overlaps) {
        const end = Math.max(0, r.start - start)
        if (end > cursor) {
          kept.push({ ...node, text: node.text.slice(cursor, end) })
        }
        cursor = Math.min(length, r.end - start)
      }
      if (cursor < length) {
        kept.push({ ...node, text: node.text.slice(cursor) })
      }
      return kept
    })
  }
  const stripItems = (items: ListItem[]): ListItem[] =>
    items.flatMap((item) => {
      const content = stripInline(item.id, item.content)
      const children = item.children ? stripItems(item.children) : undefined
      return content.length || children?.length || !analysis.ranges.has(item.id)
        ? [{ ...item, content, ...(children ? { children } : {}) }]
        : []
    })
  return blocks.flatMap((block): Block[] => {
    if (analysis.hiddenBlocks.has(block.id) || (!keepBoundaries && analysis.boundaryBlocks.has(block.id))) {
      return []
    }
    if ('content' in block) {
      const content = stripInline(block.id, block.content)
      return content.length || !analysis.ranges.has(block.id) ? [{ ...block, content }] : []
    }
    if (block.type === 'list') {
      const items = stripItems(block.items)
      return items.length ? [{ ...block, items }] : []
    }
    if (block.type === 'image' && block.caption) {
      return [{ ...block, caption: stripInline(block.id, block.caption) }]
    }
    return [block]
  })
}

/** Convert an opening typed as a paragraph once, preserving its identity and inline formatting. */
export function normalizeWritingHeadings(blocks: Block[]): Block[] {
  const result = [...blocks]
  const analysis = analyzeWritingSyntax(blocks)
  for (let i = 0; i < result.length; i++) {
    const block = result[i]
    if (
      block.type !== 'paragraph' ||
      !parseSectionOpening(getBlockText(block)) ||
      analysis.hiddenBlocks.has(block.id)
    ) {
      continue
    }
    // Backticks in a preceding block may place this paragraph in a literal code region.
    const prefix = analyzeWritingSyntax([...result.slice(0, i), { ...block, type: 'heading', level: 3 }])
    if (!prefix.sections.some((s) => s.heading.id === block.id)) {
      continue
    }
    result[i] = { ...block, type: 'heading', level: inferSectionLevel(result, i) }
  }
  return result
}

/** File publication must not bypass a section's own publication state. Apply after sanitization/glyph lookup. */
export function stripUnpublishedWritingSections(
  readerBlocks: Block[],
  authorBlocks: Block[],
  publishedIds: string[],
): Block[] {
  const published = new Set(publishedIds)
  const hidden = new Set<string>()
  for (const section of analyzeWritingSyntax(authorBlocks).sections) {
    if (!published.has(section.heading.id)) {
      authorBlocks.slice(section.start, section.end).forEach((block) => hidden.add(block.id))
    }
  }
  return readerBlocks.filter((block) => !hidden.has(block.id))
}
